import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { Message } from 'primereact/message';
import { Toast } from 'primereact/toast';
import { useRef, useState } from 'react';

export const OverlaysSection = () => {
  const [displayDialog, setDisplayDialog] = useState(false);
  const toast = useRef<Toast>(null);

  const showToast = (
    severity: 'success' | 'info' | 'warn' | 'error',
    summary: string,
    detail: string
  ) => {
    toast.current?.show({ severity, summary, detail, life: 3000 });
  };

  const showConfirm = () => {
    confirmDialog({
      message: 'Are you sure you want to proceed?',
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        toast.current?.show({
          severity: 'success',
          summary: 'Confirmed',
          detail: 'You have accepted',
          life: 3000,
        });
      },
      reject: () => {
        toast.current?.show({
          severity: 'info',
          summary: 'Rejected',
          detail: 'You have rejected',
          life: 3000,
        });
      },
    });
  };

  const dialogFooter = (
    <div className='flex gap-2'>
      <Button
        label='Cancel'
        icon='pi pi-times'
        onClick={() => setDisplayDialog(false)}
        text
      />
      <Button
        label='Save'
        icon='pi pi-check'
        onClick={() => setDisplayDialog(false)}
      />
    </div>
  );

  return (
    <div id='overlays' className='space-y-6'>
      <Toast ref={toast} />
      <ConfirmDialog />

      <div>
        <h2 className='text-2xl font-bold mb-2'>Overlays</h2>
        <p className='text-gray-600 mb-4'>
          Overlay components for dialogs, notifications, and confirmations.
        </p>
      </div>

      {/* Dialog */}
      <Card title='Dialog' className='mb-4'>
        <div className='space-y-4'>
          <div>
            <Button
              label='Show Dialog'
              icon='pi pi-external-link'
              onClick={() => setDisplayDialog(true)}
            />
            <Dialog
              header='Example Dialog'
              visible={displayDialog}
              style={{ width: '50vw' }}
              onHide={() => setDisplayDialog(false)}
              footer={dialogFooter}
            >
              <p className='text-gray-600'>
                This is an example dialog component. Dialogs are used to display
                content in an overlay window, requiring user interaction before
                returning to the main application flow.
              </p>
              <p className='text-gray-600 mt-3'>
                They can contain forms, confirmations, or any other content that
                needs focused attention.
              </p>
            </Dialog>
          </div>

          <div className='bg-gray-50 p-4 rounded border'>
            <h3 className='text-sm font-semibold mb-2'>Dialog Features</h3>
            <ul className='text-sm text-gray-600 space-y-1 list-disc list-inside'>
              <li>Modal overlay blocking background interaction</li>
              <li>Customizable header and footer</li>
              <li>Responsive width options</li>
              <li>Close button and ESC key support</li>
              <li>Draggable and resizable variants available</li>
            </ul>
          </div>
        </div>
      </Card>

      {/* ConfirmDialog */}
      <Card title='ConfirmDialog' className='mb-4'>
        <div className='space-y-4'>
          <div>
            <Button
              label='Show Confirmation'
              icon='pi pi-question-circle'
              onClick={showConfirm}
              severity='warning'
            />
          </div>
          <div className='bg-gray-50 p-4 rounded border'>
            <h3 className='text-sm font-semibold mb-2'>
              Confirmation Dialog Use Cases
            </h3>
            <ul className='text-sm text-gray-600 space-y-1 list-disc list-inside'>
              <li>Delete confirmations</li>
              <li>Unsaved changes warnings</li>
              <li>Action verifications</li>
              <li>Destructive operation alerts</li>
            </ul>
          </div>
        </div>
      </Card>

      {/* Toast */}
      <Card title='Toast Notifications' className='mb-4'>
        <div className='space-y-3'>
          <p className='text-sm text-gray-600 mb-3'>
            Toast notifications appear in the top-right corner for temporary
            feedback messages.
          </p>
          <div className='flex flex-wrap gap-2'>
            <Button
              label='Success'
              icon='pi pi-check'
              severity='success'
              onClick={() =>
                showToast(
                  'success',
                  'Success',
                  'Operation completed successfully'
                )
              }
            />
            <Button
              label='Info'
              icon='pi pi-info-circle'
              severity='info'
              onClick={() =>
                showToast(
                  'info',
                  'Information',
                  'This is an informational message'
                )
              }
            />
            <Button
              label='Warning'
              icon='pi pi-exclamation-triangle'
              severity='warning'
              onClick={() =>
                showToast(
                  'warn',
                  'Warning',
                  'Please review this warning message'
                )
              }
            />
            <Button
              label='Error'
              icon='pi pi-times-circle'
              severity='danger'
              onClick={() =>
                showToast('error', 'Error', 'An error has occurred')
              }
            />
          </div>
        </div>
      </Card>

      {/* Message */}
      <Card title='Message (Inline Alerts)' className='mb-4'>
        <div className='space-y-3'>
          <Message
            severity='success'
            text='Success: Your changes have been saved'
          />
          <Message
            severity='info'
            text='Info: New updates are available for download'
          />
          <Message
            severity='warn'
            text='Warning: Your session will expire in 5 minutes'
          />
          <Message
            severity='error'
            text='Error: Unable to connect to the server'
          />
          <Message text='Default: This is a standard message without severity' />
        </div>
      </Card>

      {/* Message Variants */}
      <Card title='Message Variants' className='mb-4'>
        <div className='space-y-4'>
          <div>
            <h3 className='text-sm font-semibold mb-3 text-gray-700'>
              Message Severity Types
            </h3>
            <div className='space-y-3'>
              <Message
                severity='success'
                text='Operation completed successfully'
              />
              <Message
                severity='info'
                text='Additional information available'
              />
              <Message severity='warn' text='This is a warning message' />
              <Message severity='error' text='This is an error message' />
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};
